package com.bulut.mecitogullari.ui.screens.dashboard

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.components.MoneyText
import com.bulut.mecitogullari.ui.components.SectionTitle
import com.bulut.mecitogullari.ui.components.StatCard
import com.bulut.mecitogullari.ui.components.TransactionRow
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun DashboardScreen(
    onAddTransaction: () -> Unit,
    onTransactionClick: (transactionId: String) -> Unit,
    onSeeAllTransactions: () -> Unit,
    viewModel: DashboardViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier.fillMaxSize().statusBarsPadding(),
            contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 96.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            item {
                Column {
                    Text(
                        text = if (uiState.userName.isNotBlank()) {
                            "${strings.greeting}, ${uiState.userName}"
                        } else {
                            strings.greeting
                        },
                        style = MaterialTheme.typography.titleLarge,
                    )
                    Text(
                        text = buildString {
                            if (uiState.companyName.isNotBlank()) {
                                append(uiState.companyName)
                                append("  •  ")
                            }
                            append(strings.cashSummaryTemplate.replace("{month}", uiState.month.label))
                        },
                        style = MaterialTheme.typography.bodyMedium,
                        color = AppColors.TextSecondary,
                    )
                }
            }

            item {
                NetProfitCard(
                    net = uiState.summary.netProfit,
                    closing = uiState.summary.closingBalance,
                )
            }

            item {
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    StatCard(
                        title = strings.statCarryOver,
                        amount = uiState.summary.openingBalance,
                        modifier = Modifier.weight(1f),
                    )
                    StatCard(
                        title = strings.statMonthIncome,
                        amount = uiState.summary.income,
                        amountColor = AppColors.Income,
                        modifier = Modifier.weight(1f),
                    )
                    StatCard(
                        title = strings.statMonthExpense,
                        amount = uiState.summary.expense,
                        amountColor = AppColors.Expense,
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SectionTitle(strings.recentTransactions)
                    TextButton(onClick = onSeeAllTransactions) { Text(strings.seeAll) }
                }
            }

            if (uiState.recentTransactions.isEmpty()) {
                item {
                    EmptyState(strings.dashboardEmpty)
                }
            } else {
                items(uiState.recentTransactions, key = { it.id }) { transaction ->
                    TransactionRow(
                        transaction = transaction,
                        projectName = transaction.projectId?.let { uiState.projectNames[it] },
                        onClick = { onTransactionClick(transaction.id) },
                    )
                }
            }
        }

        FloatingActionButton(
            onClick = onAddTransaction,
            containerColor = AppColors.Navy,
            contentColor = Color.White,
            modifier = Modifier.align(Alignment.BottomEnd).padding(20.dp),
        ) {
            Icon(Icons.Default.Add, contentDescription = strings.addTransaction)
        }
    }
}

@Composable
private fun NetProfitCard(net: Double, closing: Double) {
    val positive = net >= 0
    Card(
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (positive) AppColors.Income else AppColors.Expense,
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text(
                text = LocalStrings.current.netProfitTitle,
                style = MaterialTheme.typography.labelLarge,
                color = Color.White.copy(alpha = 0.85f),
            )
            MoneyText(
                amount = net,
                color = Color.White,
                large = true,
                modifier = Modifier.padding(top = 4.dp),
            )
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 10.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(
                    text = LocalStrings.current.closingBalanceLabel,
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White.copy(alpha = 0.85f),
                )
                Text(
                    text = com.bulut.mecitogullari.core.util.formatAmount(closing),
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.White,
                )
            }
        }
    }
}
